import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FinanceService } from '@app/pages/authenticated/pages/finance/finance.service';
import { BannerNotificationsService } from '@app/shared/services/banner-notifications.service';
import { ModalConfirmationComponent } from '@app/shared/components/modal-confirmation/modal-confirmation.component';
import { getRetryButtonLabel, isRetryVisible } from '../courier-invoice-outcome.rules';

/**
 * Retry for one Uber recon_audits row, shared by the Courier Invoices table and the detail page.
 * Renders nothing when Retry is not offered for the row (see `isRetryVisible`); the caller decides
 * whether the viewer may modify finance at all. Emits `retried` once app-services accepted it.
 */
@Component({
  standalone: false,
  selector: 'app-courier-invoice-retry-button',
  templateUrl: './courier-invoice-retry-button.component.html',
})
export class CourierInvoiceRetryButtonComponent {
  @Input() invoice: any;
  @Output() retried = new EventEmitter<void>();

  // True only while this row's Retry request is in flight — disables the button.
  public retrying = false;

  constructor(
    private financeService: FinanceService,
    private bannerNotificationsService: BannerNotificationsService,
    private dialog: MatDialog
  ) {}

  public get visible(): boolean {
    return isRetryVisible(this.invoice);
  }

  public get label(): string {
    return getRetryButtonLabel(this.invoice);
  }

  // Same button, same endpoint, two different confirm messages: a processed_with_failures
  // row re-runs only the rows that failed; a stuck pending/processing row re-runs whatever hasn't
  // finished yet (it may have no rows_failed recorded at all).
  public confirmRetry(): void {
    const invoice = this.invoice;
    const isStuckRow = invoice.status === 'pending' || invoice.status === 'processing';
    const label = this.label;
    const dialogRef = this.dialog.open(ModalConfirmationComponent, {
      width: '600px',
      data: {
        title: label,
        content: isStuckRow
          ? `Are you sure you want to retry invoice ${invoice.invoice_number}? ` +
            'This re-runs the rows that have not finished processing — it does not create a new upload.'
          : `Are you sure you want to retry the failed rows for invoice ${invoice.invoice_number}? ` +
            'This re-runs only the rows that failed on the same invoice — it does not create a new upload.',
        confirmBtnLabel: label,
        btnColor: 'primary',
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.runRetry(invoice);
      }
    });
  }

  private runRetry(invoice: any): void {
    this.retrying = true;
    this.financeService.retryFailedRows(invoice.id).subscribe({
      next: (res) => {
        this.retrying = false;
        if (res?.code === 'enqueue-unconfirmed') {
          this.bannerNotificationsService.success('Retry submitted — awaiting confirmation. Refresh in a minute.');
        } else {
          this.bannerNotificationsService.success('Retry started. Reconciliation is running in the background.');
        }
        this.retried.emit();
      },
      error: (err: any) => {
        this.retrying = false;
        const message = typeof err === 'string' ? err : err?.message || 'Could not start the retry';
        this.bannerNotificationsService.error(message, {
          category: 'POST retry-failed error',
          message: err,
        });
      },
    });
  }
}
